import Cookies from 'js-cookie'
import { useUser } from '@/lib/hooks'
import { EventBus, Events } from '~/lib/events'
import logger from '@/lib/logger'
import { LTStorageKeys } from '~/lib/events/tracker'
import { useLocalStorage } from '~/lib/helpers/storage'
import ProgressContent from '@/content/progress.json'

export const state = () => ({
  lastLogoVisited: null,
  progress: [],
  orders: [],
})

export const actions = {
  async fetchOrders({ commit }) {
    const { data } = await this.$api.get('/api/v2/orders', {
      params: { status: 'COMPLETE' },
    })

    commit('setOrders', data.data)
  },

  async replaceOrders({ rootState, dispatch }) {
    const hasCartItems = rootState.cart.items.length > 0

    // At this point, the cart items are correctly updated to the new currency (using fetchCart(true))

    if (hasCartItems) {
      await dispatch('createOrders')
    }
  },

  async createOrders({ rootState, commit }) {
    const tap_ref = Cookies.get('ref')
    const tap_vid = Cookies.get('tap_vid')
    const sscid = Cookies.get('sscid')
    const referrer = Cookies.get('logoReferrer')
    const user_agent = window.navigator.userAgent.toLowerCase()

    const localStorage = useLocalStorage()

    const session_id = localStorage.getItem(LTStorageKeys.SESSION_ID)
    const anonymous_id = localStorage.getItem(LTStorageKeys.ANONYMOUS_ID)
    const referring_product = localStorage.getItem(
      LTStorageKeys.REFERRING_PRODUCT
    )
    const country_code = localStorage.getItem(LTStorageKeys.COUNTRY_CODE)
    const adblocker_detected = !window.ga || (!!window.ga && !window.ga.loaded)

    const usedFeatureFlags = rootState.flags.updated_flags.reduce(
      (acc, flag) => {
        acc[flag] = rootState.flags[flag]

        return acc
      },
      {}
    )

    const checkoutParams = {
      cart: rootState.cart.id,
      items: rootState.cart.items,
      custom_checkout: true,
      enable_tax: true,
      metadata: {
        flags: usedFeatureFlags,
        tap_ref,
        tap_vid,
        referrer,
        sscid,
        user_agent,
        session_id,
        anonymous_id,
        referring_product,
        country_code,
        adblocker_detected,
      },
    }

    const { data } = await this.$api.post('/api/v2/orders', checkoutParams)

    Cookies.set('processing_order', data.id)
    commit('setOrders', data.data)
  },

  async fetchProgress({ rootState, commit }) {
    // Reset the progress so we get the loading UI again!
    commit('setProgress', [])

    try {
      const { getUser } = useUser(this.$api)
      const { id } = await getUser()

      const { data } = await this.$api.get(`/api/v3/users/${id}/progress`, {
        params: { group: 'tutorial' },
      })

      /**
       * Order steps based on array from progress.json
       */
      const allSteps = data.data
      const stepOrder = ProgressContent.steps
      let orderedSteps = []
      stepOrder.forEach(stepSlug => {
        const stepIndex = allSteps.findIndex(step => step.slug === stepSlug)
        if (stepIndex !== -1) {
          orderedSteps.push(allSteps[stepIndex])
        }
      })

      if (!rootState.flags.enable_one_page_site_creation) {
        orderedSteps = orderedSteps.filter(
          step => step.slug !== 'one-page-site'
        )
      }

      // Combine API progress with our local steps
      commit('setProgress', [
        { label: 'Business Name', completed: true, path: 'business-name' },
        { label: 'Logo Design', completed: true, path: 'editor/colors', logo_query: 'editing_logo' },
        ...orderedSteps,
      ])
    } catch (error) {
      logger.error(error)
      console.error(error)
    }
  },

  async markStepAsComplete({ rootState, state, commit, getters }, slug) {
    if (!rootState.flags.enable_progress_tracker) {
      return
    }
    const step = state.progress.find(step => step.slug === slug)

    if (!step || step.completed) {
      return
    }

    try {
      const { getUser } = useUser(this.$api)
      const { id } = await getUser()

      const { data: event } = await this.$api.post(
        `/api/v3/users/${id}/progress/track`,
        {
          step_id: step.id,
        }
      )

      commit('completeProgressStep', { step, event })

      EventBus.$emit(Events.ProgressStepCompleted, { step })
    } catch (error) {
      logger.error(error)
      console.error(error)
    }

    const completedAllSteps = getters.steps.every(step => step.completed)

    if (completedAllSteps) {
      EventBus.$emit(Events.AllProgressStepsCompleted)
    }
  },
}

export const mutations = {
  setOrders(state, orders) {
    state.orders = orders
  },

  /**
   * Store our Progress steps.
   *
   * @param {Object} state the VueX state, passed in by Nuxt
   * @param {Array} progress an Array of progress steps from the API
   */
  setProgress(state, progress) {
    state.progress = progress
  },

  setLastLogoVisited(state, logo) {
    state.lastLogoVisited = logo
  },

  completeProgressStep(state, { step, event }) {
    step.completed = true
    step.events.push(event)
    state.progress = [...state.progress.filter(s => s.id !== step.id), step]
  },
}

export const getters = {
  /**
   * Build a list of steps, removing any that are disabled via flags or the Enterprise.
   * We're using a Reduce instead of a Filter in order to also return the matching
   * Feature for each step.
   *
   * @returns {Array}
   */
  steps(state, _getters, _rootState, rootGetters) {
    return state.progress.reduce((steps, step) => {
      // Only check if the feature is actually a feature (has a slug)
      if (step.slug) {
        // Get the full feature
        const feature = rootGetters['app/getFeatureById'](step.slug)

        // If it's enabled, return the full feature to access the description etc
        if (rootGetters['app/featureIsEnabled'](feature)) {
          steps.push({ ...step, ...feature })
        }
      } else {
        // For steps without a slug (probably fake local ones), just return them
        steps.push(step)
      }

      return steps
    }, [])
  },
}
